import React from 'react';
import Card from '../../../components/ui/Card';
import Icon from '../../../components/AppIcon';
import Badge from '../../../components/ui/Badge';

const DynamicPricingSuggestions = ({ suggestions = [], aiAnalysis = null, loading = false }) => {
  const getAdjustmentColor = (percentage) => {
    if (percentage > 0) return 'text-success-600';
    if (percentage < 0) return 'text-danger-600';
    return 'text-muted-foreground';
  };

  const getAdjustmentBadge = (percentage) => {
    if (percentage > 0) return 'success';
    if (percentage < 0) return 'danger';
    return 'default';
  };

  const getAdjustmentIcon = (percentage) => {
    if (percentage > 0) return 'TrendingUp';
    if (percentage < 0) return 'TrendingDown';
    return 'Minus';
  };

  return (
    <div className="space-y-6">
      {/* AI Strategy Overview */}
      {aiAnalysis?.overallStrategy && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-success-100 rounded-lg">
              <Icon name="Brain" className="w-5 h-5 text-success-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">OpenAI Pricing Strategy</h3>
              <p className="text-sm text-muted-foreground">GPT-5 Powered Market Analysis</p>
            </div>
          </div>

          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Target" className="w-4 h-4" />
              Overall Market Strategy
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {aiAnalysis?.overallStrategy}
            </p>
          </div>
        </Card>
      )}

      {loading && !aiAnalysis && (
        <Card className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Generating pricing recommendations with OpenAI...</p>
            </div>
          </div>
        </Card>
      )}

      {/* Pricing Recommendations */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Unit-Specific Pricing Recommendations</h3>
        {aiAnalysis?.recommendations?.length > 0 ? (
          <div className="space-y-4">
            {aiAnalysis?.recommendations?.map((rec, index) => {
              const unit = suggestions?.find(s => s?.id === rec?.unitId);
              return (
                <div key={index} className="bg-muted border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-foreground">
                          {unit?.unitNumber || `Unit ${rec?.unitId}`}
                        </h4>
                        <Badge variant={getAdjustmentBadge(rec?.adjustmentPercentage)}>
                          <Icon name={getAdjustmentIcon(rec?.adjustmentPercentage)} className="w-3 h-3 mr-1" />
                          {rec?.adjustmentPercentage > 0 ? '+' : ''}{rec?.adjustmentPercentage?.toFixed(1)}%
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {unit?.sqft ? `${unit?.sqft} sqft` : ''} 
                        {unit?.floor ? `• Floor ${unit?.floor}` : ''}
                        {unit?.status ? `• ${unit?.status}` : ''}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="bg-white rounded-lg p-3 border border-border">
                      <div className="text-xs text-muted-foreground mb-1">Current Price</div>
                      <div className="text-xl font-bold text-foreground">
                        ${rec?.currentPrice?.toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-success-50 border border-success-200 rounded-lg p-3">
                      <div className="text-xs text-muted-foreground mb-1">Suggested Price</div>
                      <div className="text-xl font-bold text-success-600">
                        ${rec?.suggestedPrice?.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="bg-white bg-opacity-50 rounded-lg p-3">
                      <h5 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
                        <Icon name="MessageSquare" className="w-4 h-4" />
                        AI Reasoning
                      </h5>
                      <p className="text-sm text-muted-foreground">
                        {rec?.reasoning}
                      </p>
                    </div>

                    <div className="bg-white bg-opacity-50 rounded-lg p-3">
                      <h5 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
                        <Icon name="TrendingUp" className="w-4 h-4" />
                        Expected Impact
                      </h5>
                      <p className="text-sm text-muted-foreground">
                        {rec?.expectedImpact}
                      </p>
                    </div>

                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
                      <h5 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
                        <Icon name="Clock" className="w-4 h-4" />
                        Optimal Timing
                      </h5>
                      <p className="text-sm text-primary-700 font-medium">
                        {rec?.timing}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="DollarSign" className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No pricing suggestions available</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default DynamicPricingSuggestions;